import { Checkbox, Dropdown } from 'antd';
import { CaretDownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { FileColumn, PropertyMapping, TaskonioField } from '../../../../types/Import';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';
import colorTokens from '../../../../constants/design_system/colors';
import { ConfigProvider } from 'antd';

interface ImportMappingPropertiesProps {
  propertyMappings: PropertyMapping[];
  fileColumns: FileColumn[];
  autoMappedFields: Set<TaskonioField>;
  addUnsorted: boolean;
  onAddUnsortedChange: (checked: boolean) => void;
  onMappingChange: (taskonioField: TaskonioField, fileColumnIndex: number | null) => void;
}

const TASKONIO_FIELD_CONFIG: Record<TaskonioField, { label: string; tooltip?: string }> = {
  id: { label: 'ID', tooltip: 'Original task ID from the source tool' },
  taskName: { label: 'Task Name', tooltip: 'Required. Maps to the task title' },
  subitem: { label: 'Subitem', tooltip: 'Parent task ID for creating subtasks' },
  epic: { label: 'Epic', tooltip: 'Epic or project grouping' },
  status: { label: 'Status', tooltip: 'Task status (matched to your workspace statuses)' },
  priority: { label: 'Priority', tooltip: 'Priority level (No priority, Low, Medium, High, Urgent)' },
  estimate: { label: 'Estimate', tooltip: 'Time estimate for the task' },
  storyPoints: { label: 'Story Points', tooltip: 'Story point estimate' },
  assignee: { label: 'Owner', tooltip: 'Task owner (matched via user mapping below)' },
  startDate: { label: 'Start Date', tooltip: 'Task start date' },
  deadline: { label: 'Target Date', tooltip: 'Task due date or target date' },
  description: { label: 'Description', tooltip: 'Task description or details' },
  type: { label: 'Stage', tooltip: 'Task stage (epic, item, subitem)' },
  labels: { label: 'Label', tooltip: 'Labels or tags (comma-separated values supported)' },
};

export const ImportMappingProperties = ({
  propertyMappings,
  fileColumns,
  autoMappedFields,
  addUnsorted,
  onAddUnsortedChange,
  onMappingChange,
}: ImportMappingPropertiesProps) => {
  const getColumnLabel = (fileColumnIndex: number | null) => {
    if (fileColumnIndex === null) return 'Choose';
    const col = fileColumns.find((c) => c.index === fileColumnIndex);
    return col?.name ?? 'Choose';
  };

  const buildMenuItems = (currentField: TaskonioField) => [
    {
      key: 'none',
      label: 'Choose',
      onClick: () => onMappingChange(currentField, null),
    },
    ...fileColumns.map((col) => ({
      key: String(col.index),
      label: col.name,
      onClick: () => onMappingChange(currentField, col.index),
    })),
  ];

  return (
    <div className="mb-4">
      <div className="text-brand-grey-dark text-sm font-semibold mb-3">Mapping</div>

      <div className="bg-neutral-100 rounded-lg p-4">
      <div className="text-brand-grey-dark text-sm font-semibold mb-2">Properties</div>
      <div className="flex items-center pb-2">
        <span className="text-neutral-700 text-xs flex-1">Taskonio</span>
        <span className="text-neutral-700 text-xs">Properties from your file</span>
      </div>

      <div className="flex flex-col">
        {propertyMappings.map((mapping) => {
          const config = TASKONIO_FIELD_CONFIG[mapping.taskonioField];
          const isMapped = mapping.fileColumnIndex !== null;

          return (
            <div
              key={mapping.taskonioField}
              className="flex items-center justify-between py-2 border-b border-neutral-200 last:border-b-0"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-brand-grey-dark text-sm">
                  {config.label}
                </span>
                {config.tooltip && (
                  <CustomTooltip title={config.tooltip}>
                    <InfoCircleOutlined className="text-neutral-500 text-xs cursor-default" />
                  </CustomTooltip>
                )}
              </div>
              <Dropdown
                menu={{ items: buildMenuItems(mapping.taskonioField), style: { maxHeight: 400, overflowY: 'auto' } }}
                trigger={['click']}
              >
                <button className="flex items-center gap-1.5 bg-transparent border-none cursor-pointer py-0.5">
                  <span className={`text-sm ${isMapped ? 'text-brand-grey-dark' : 'text-neutral-500'}`}>
                    {getColumnLabel(mapping.fileColumnIndex)}
                  </span>
                  <CaretDownOutlined className="text-neutral-500 text-[10px]" />
                </button>
              </Dropdown>
            </div>
          );
        })}
      </div>

      <ConfigProvider
        theme={{
          components: {
            Checkbox: {
              colorPrimary: colorTokens.blue[600],
              colorPrimaryHover: colorTokens.blue[700],
            },
          },
        }}
      >
        <div className="mt-4 mb-2">
          <Checkbox checked={addUnsorted} onChange={(e) => onAddUnsortedChange(e.target.checked)}>
            <span className="text-brand-grey-dark text-sm">Add unsorted columns to the Task descriptions</span>
          </Checkbox>
        </div>
      </ConfigProvider>
      </div>
    </div>
  );
};
